import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import hairSkinIntroImage from './blogimg/HairSkinNailsvsSupplements/hair_skin_intro.jpg';
import nutrientsImage from './blogimg/HairSkinNailsvsSupplements/nutrients.jpg';
import supplementsWorkImage from './blogimg/HairSkinNailsvsSupplements/supplements_work.jpg';
import balancedDietImage from './blogimg/HairSkinNailsvsSupplements/balanced_diet.jpg';
import supplementsVsDietImage from './blogimg/HairSkinNailsvsSupplements/supplements_vs_diet.jpg';
import conclusionImage from './blogimg/HairSkinNailsvsSupplements/conclusion.jpg';

/**
 * Helper function to map Firestore image filenames to local image imports.
 */
function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'hair_skin_intro.jpg':
      return hairSkinIntroImage;
    case 'nutrients.jpg':
      return nutrientsImage;
    case 'supplements_work.jpg':
      return supplementsWorkImage;
    case 'balanced_diet.jpg':
      return balancedDietImage;
    case 'supplements_vs_diet.jpg':
      return supplementsVsDietImage;
    case 'conclusion.jpg':
      return conclusionImage;
    default:
      return null;
  }
}

const HairSkinNailsSupplementsvsBalancedDietPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Hair, Skin & Nails Supplements vs. Balanced Diet: Which Works Best? (Science-Backed)";

    // Helper function to add/update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Discover whether hair, skin & nails supplements or a balanced diet delivers the best results. Learn science-backed ways to enhance beauty from within."
    );
    setMetaTag(
      "keywords",
      "hair skin nails supplements, balanced diet, biotin, collagen, vitamins, beauty, nutrition, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Best Hair, Skin & Nails Vitamins vs. Balanced Diet: Which Works Best? (Science-Backed Answer)",
      true
    );
    setMetaTag(
      "og:description",
      "Do hair, skin, and nails vitamins really work, or is a balanced diet enough? Uncover the science behind supplements, key nutrients, and the best approach for healthy, glowing skin, strong nails, and vibrant hair.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/assets/images/hair_skin_intro.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/best-hair-skin-nails-vitamins-vs-balanced-diet",
      true
    );

    // Twitter card
    setMetaTag("twitter:card", "summary_large_image");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
    };
  }, []);

  // 2️⃣ Fetch Article Data from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        // Firestore path: /products/blog/HairSkinNailsSupplementsvsBalancedDiet
        const colRef = collection(db, 'products', 'blog', 'HairSkinNailsSupplementsvsBalancedDiet');
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
          // console.warn('No blog document found for HairSkinNailsSupplementsvsBalancedDiet');
        }
      } catch (error) {
        // console.error('Error fetching blog article:', error);
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Organize Sections for Conclusion/References
  let conclusionSection = null;
  let referenceBlock = null;
  let otherBlocks = [];

  if (article.sections && article.sections.length > 0) {
    article.sections.forEach((section, sectionIndex) => {
      if (section.blocks && section.blocks.length > 0) {
        // If the first block is an h2 titled "Conclusion", store that section
        if (section.blocks[0].tag === 'h2' && section.blocks[0].text === 'Conclusion') {
          conclusionSection = section; 
        } 
        // If it's the last section, treat it as references
        else if (sectionIndex === article.sections.length - 1) {
          referenceBlock = section.blocks; 
        } 
        else {
          otherBlocks.push(section);
        }
      }
    });
  }

  // 4️⃣ Render Block Function
  const renderBlock = (block, index, isReference = false) => {
    const { tag, text } = block;
    const style = isReference ? { fontSize: '10px', margin: '0' } : {};

    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1" style={style}>
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );

      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2" style={style}>
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );

      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3" style={style}>
              {text}
            </h3>
          </React.Fragment>
        );

      case 'p':
        return (
          <p key={index} className="recipe-p" style={style}>
            {text}
          </p>
        );

      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }

      case 'ul':
        return (
          <ul key={index} className="recipe-ul" style={style}>
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );

      case "ol":
        return (
          <ol key={index} className="recipe-ol" style={style}>
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === "object") {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );

      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );

      default:
        return (
          <div key={index} style={style}>
            {text}
          </div>
        );
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 5️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 from Firestore data (if any) */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}

        {/* Render other blocks (excluding Conclusion & References) */}
        {otherBlocks.map((section, sectionIndex) => (
          <section key={sectionIndex}>
            {section.blocks &&
              section.blocks.map((block, blockIndex) =>
                renderBlock(block, blockIndex)
              )}
          </section>
        ))}

        {/* Render Conclusion Section */}
        {conclusionSection && (
          <section>
            {conclusionSection.blocks.map((block, index) =>
              renderBlock(block, index)
            )}
          </section>
        )}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() =>
            (window.location.href =
              'https://purehealthnexus.com/top-rated-vitamins')
          }
        >
          See Top-Rated Vitamins Here
        </button>

        {/* Render Reference Block (last blocks) */}
        {referenceBlock &&
          referenceBlock.map((block, index) => (
            <div className="notranslate" key={index}>
              {renderBlock(block, index, true)}
            </div>
          ))}
        </div>
    </div>
  );
};

export default HairSkinNailsSupplementsvsBalancedDietPage;
